import { loadModules } from 'esri-loader';
import React from 'react';
import ReactDOM from 'react-dom';
import BSLAExternalLinkComponent from '../../helpers/BSLAExternalLinkComponent';
import BSLAPopupItemComponent from '../../helpers/BSLAPopupItemComponent';
import { sortInfos } from '../landchars/BSLAConsEaseLayers';

const sarasotaPopupContent = (feature: __esri.Feature) => {
    const atts = feature.graphic.attributes;
    const node = document.createElement('div');
    const content = (
        <React.Fragment>
            <BSLAPopupItemComponent label="Feature ID" value={atts.FeatureID} />
            <BSLAPopupItemComponent label="Group Year" value={atts.GroupYear} />
            <BSLAPopupItemComponent label="Population Code" value={atts.MetaPopCode} />
            <BSLAPopupItemComponent label="Coded ID" value={atts.CodedID ? atts.CodedID : 'N/A'} />
            <BSLAPopupItemComponent label="Population Name" value={atts.PopulationName} />
            <BSLAPopupItemComponent label="Population Label" value={atts.PopulationLabel} />
            <BSLAPopupItemComponent label="Area ID" value={atts.AreaID} />
            <p className="mt-3 mb-1">
                Scrub jay groups were identified in county-wide surveys in 2000 and 2004. Scrub jays may still inhabit the areas and consultation with the US Fish and Wildlife
                Service is required within 850 feet of documented scrub jay sightings.
            </p>
            <BSLAExternalLinkComponent href="https://www.scgov.net/EnvironmentalPlanning/Pages/Scrubjay.aspx" />
        </React.Fragment>
    );

    ReactDOM.render(content, node);

    return node;
};

const sarasotaHabitatPopupContent = (feature: __esri.Feature) => {
    const atts = feature.graphic.attributes;
    const node = document.createElement('div');
    const content = (
        <React.Fragment>
            <BSLAPopupItemComponent label="Feature ID" value={atts.FeatureID} />
            <BSLAPopupItemComponent label="Group Year" value={atts.GroupYear} />
            <BSLAPopupItemComponent label="Core ID" value={atts.CodedID ? atts.CORID : 'N/A'} />
            <BSLAPopupItemComponent label="Population Code" value={atts.MetaPopCode} />
            <BSLAPopupItemComponent label="Coded ID" value={atts.CodedID ? atts.CodedID : 'N/A'} />
            <BSLAPopupItemComponent label="Population Label" value={atts.PopulationLabel} />
            <BSLAPopupItemComponent label="Population Name" value={atts.PopulationName} />
            <BSLAPopupItemComponent label="Buffer Distance (ft)" value={atts.BUFF_DIST} />
            <BSLAPopupItemComponent label="Area ID" value={atts.AreaID} />
            <BSLAPopupItemComponent label="Analysis Area" value={atts.AnalysisArea} />
        </React.Fragment>
    );

    ReactDOM.render(content, node);

    return node;
};

const northportPopupContent = (feature: __esri.Feature) => {
    const atts = feature.graphic.attributes;
    const node = document.createElement('div');
    const content = (
        <React.Fragment>
            <BSLAPopupItemComponent label="Affected Parcel ID" value={atts.PID} />
            <p className="mt-1">This layer shows parcels with possible Scrub Jay Habitat as tracked by the City of North Port.</p>
        </React.Fragment>
    );

    ReactDOM.render(content, node);

    return node;
};

const sarasotaZonesPopupContent = (feature: __esri.Feature) => {
    const atts = feature.graphic.attributes;
    const node = document.createElement('div');
    const content = (
        <React.Fragment>
            <BSLAPopupItemComponent label="Population Name" value={atts.PopulationName} />
            <p className="mt-1">
                Scrub jay groups were identified in county-wide surveys in 2000 and 2004. Scrub jays may still inhabit the areas and consultation with the US Fish and Wildlife
                Service is required within 850 feet of documented scrub jay sightings.
            </p>
            <div className="mt-1">
                <BSLAExternalLinkComponent href="https://www.scgov.net/EnvironmentalPlanning/Pages/Scrubjay.aspx" />
            </div>
        </React.Fragment>
    );

    ReactDOM.render(content, node);

    return node;
};

const fwcPopup = (feature: __esri.Feature) => {
    const node = document.createElement('div');
    const content = (
        <React.Fragment>
            <p>
                Issue: Impacts to State and Federally protected Scrub Jay Habitat mapped by Florida Fish and Wildlife Conservation Commission (FWC) may require surveys, lengthy
                permitting time frames, and significant mitigation costs.
            </p>
            <p>
                Cost: While a simple assessment by an environmental consultant could cost as little as $500 to determine that a formal survey is not required, permitting occupied
                Scrub Jay habitat can take years and cost $25,000 or more. Additional mitigation requirements can significantly restrict buildable area or cost $20,000 - $70,000
                per acre of area impacted.
            </p>
            <div className="mt-1">
                <BSLAExternalLinkComponent href="http://myfwc.com/wildlifehabitats/imperiled/profiles/birds/florida-scrub-jay" />
            </div>
        </React.Fragment>
    );

    ReactDOM.render(content, node);

    return node;
};

const charlottePopup = (feature: __esri.Feature) => {
    const node = document.createElement('div');
    const content = (
        <React.Fragment>
            <p className="mb-1">
                Issue: Traditional Scrub Jay Permitting requires the completion of a Scrub Jay Survey. However, Charlotte County has been approved by the U.S. Fish and Wildlife
                Agency to utilize the Habitat Conservation Plan, which quickens permitting and eliminates the survey requirement.
            </p>
            <p className="mb-1">
                Cost: The size of the parcel determines the fee and minimization criteria required to develop on a parcel, for example the fee for a 10,000 sq. ft. lot is $2,200.
                However, there are some general rules that can be used as guidelines:
            </p>
            <ul className="mb-1">
                <li>Parcels less than 3-acres in size</li>
                <ul>
                    <li>You cannot clear during nesting season, which lasts from March 1st through June 30th.</li>
                    <li>Recommendation to plant Scrub Oaks on the parcel after the completion of construction.</li>
                </ul>
                <li>Parcels larger than 3-acres in size are more complex, and requires additional review from Charlotte County.</li>
            </ul>
            <p>In either case, please contact Charlotte County to determine the exact requirements for your property.</p>
            <div className="mt-1">
                <BSLAExternalLinkComponent href="http://www.charlottecountyfl.com/CommunityServices/NaturalResources/ScrubJays/HCP/index.asp" />
            </div>
        </React.Fragment>
    );

    ReactDOM.render(content, node);

    return node;
};

const scrubJayInfos = [
    {
        title: 'Scrub Jay Groups (Sarasota)',
        url: 'https://utility.arcgis.com/usrsvcs/servers/7a85ab42f6d54a3a97c42bc6456bc096/rest/services/SCRUB_JAY_V2/FeatureServer/0',
        popupContent: { content: sarasotaPopupContent }
    },
    {
        title: 'Scrub Jay Habitat (Sarasota)',
        url: 'https://utility.arcgis.com/usrsvcs/servers/7a85ab42f6d54a3a97c42bc6456bc096/rest/services/SCRUB_JAY_V2/FeatureServer/1',
        popupContent: { content: sarasotaHabitatPopupContent }
    },
    {
        title: 'Scrub Jay Habitat (FWC)',
        url: 'https://utility.arcgis.com/usrsvcs/servers/7a85ab42f6d54a3a97c42bc6456bc096/rest/services/SCRUB_JAY_V2/FeatureServer/2',
        popupContent: { content: fwcPopup }
    },
    {
        title: 'Scrub Jay Permit Boundary (Charlotte)',
        url: 'https://utility.arcgis.com/usrsvcs/servers/7a85ab42f6d54a3a97c42bc6456bc096/rest/services/SCRUB_JAY_V2/FeatureServer/3',
        popupContent: { content: charlottePopup }
    },
    {
        title: 'Scrub Jay Zones (Sarasota)',
        url: 'https://utility.arcgis.com/usrsvcs/servers/7a85ab42f6d54a3a97c42bc6456bc096/rest/services/SCRUB_JAY_V2/FeatureServer/4',
        popupContent: { content: sarasotaZonesPopupContent }
    },
    {
        title: 'Scrub Jay Parcels (City of North Port)',
        url: 'https://utility.arcgis.com/usrsvcs/servers/7a85ab42f6d54a3a97c42bc6456bc096/rest/services/SCRUB_JAY_V2/FeatureServer/5',
        popupContent: { content: northportPopupContent }
    }
];

const BSLAScrubJayLayers = async () => {
    type layerModules = [typeof import('esri/layers/GroupLayer'), typeof import('esri/layers/FeatureLayer')];

    const [GroupLayer, FeatureLayer] = await (loadModules(['esri/layers/GroupLayer', 'esri/layers/FeatureLayer']) as Promise<layerModules>);

    const layers = scrubJayInfos.sort(sortInfos).map((i) => {
        return new FeatureLayer({
            url: i.url,
            title: i.title,
            visible: false,
            popupTemplate: {
                outFields: ['*'],
                title: i.title,
                ...i.popupContent
            }
        });
    });

    return new GroupLayer({
        layers: layers,
        title: 'Scrub Jays (Various)'
    });
};

export default BSLAScrubJayLayers;
